import React, { useState } from 'react';

const EventSimulator = () => {
  const [selectedEvent, setSelectedEvent] = useState('message');
  const [eventPayload, setEventPayload] = useState('');
  const [response, setResponse] = useState('');
  const [history, setHistory] = useState<Array<{
    timestamp: string;
    event: string;
    status: 'success' | 'error';
  }>>([]);

  const eventTypes = [
    {
      id: 'message',
      name: 'Message Event',
      description: 'User sends a message',
      sample: {
        type: 'message',
        channel: 'C024BE91L',
        user: 'U2147483697',
        text: 'Hello Claude!',
        ts: '1355517523.000005'
      }
    },
    {
      id: 'app_mention',
      name: 'App Mention',
      description: 'Someone mentions your app',
      sample: {
        type: 'app_mention',
        user: 'U061F7AUR',
        text: '<@U0LAN0Z89> what is the weather in San Francisco?',
        ts: '1515449522.000016',
        channel: 'C0LAN2Q65'
      }
    },
    {
      id: 'slash_command',
      name: 'Slash Command',
      description: 'User invokes a slash command',
      sample: {
        command: '/weather',
        text: 'San Francisco',
        response_url: 'https://hooks.slack.com/commands/1234/5678',
        user_id: 'U0G9QF9C6',
        channel_id: 'C0G9QF9GZ'
      }
    },
    {
      id: 'interactive',
      name: 'Interactive Component',
      description: 'User interacts with a button or select menu',
      sample: {
        type: 'block_actions',
        user: { id: 'U0CA5', username: 'amy' },
        actions: [{
          type: 'button',
          action_id: 'approve_request',
          value: 'request_123456'
        }]
      }
    },
    {
      id: 'workflow_step',
      name: 'Workflow Step',
      description: 'Workflow step execution',
      sample: {
        type: 'workflow_step_execute',
        callback_id: 'copy_review',
        workflow_step: {
          workflow_instance_id: 'W1234567890',
          inputs: {
            submitter: { value: 'U0123456' },
            title: { value: 'Test Request' }
          }
        }
      }
    },
    {
      id: 'view_submission',
      name: 'Modal Submission',
      description: 'User submits a modal',
      sample: {
        type: 'view_submission',
        view: {
          callback_id: 'gratitude-modal',
          state: {
            values: {
              note: {
                message: { type: 'plain_text_input', value: 'Thanks!' }
              }
            }
          }
        }
      }
    }
  ];

  const simulateEvent = async () => {
    const event = eventTypes.find(e => e.id === selectedEvent);
    if (!event) return;

    try {
      const payload = eventPayload || JSON.stringify(event.sample, null, 2);

      setResponse(`Simulating ${event.name}...\n\n` +
        `Payload sent:\n${payload}\n\n` +
        `Expected Claude response:\n` +
        `"Acknowledged ${event.name} event. Processing..."`);

      setHistory(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        event: event.name,
        status: 'success'
      }]);
    } catch (error) {
      setResponse(`Error: ${error}`);
      setHistory(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        event: event.name,
        status: 'error'
      }]);
    }
  };

  const currentEvent = eventTypes.find(e => e.id === selectedEvent);

  return (
    <div className="space-y-6">
      {/* Event Type Selector */}
      <div>
        <h3 className="text-xl font-bold text-green-400 mb-4">Select Event Type</h3>
        <div className="grid md:grid-cols-3 gap-3">
          {eventTypes.map((event) => (
            <button
              key={event.id}
              onClick={() => {
                setSelectedEvent(event.id);
                setEventPayload(JSON.stringify(event.sample, null, 2));
              }}
              className={`p-4 rounded-lg border text-left transition-all ${
                selectedEvent === event.id
                  ? 'bg-green-900 border-green-400 text-green-300'
                  : 'bg-gray-800 border-gray-600 hover:border-gray-500'
              }`}
            >
              <div className="font-bold mb-1">{event.name}</div>
              <div className="text-sm text-gray-400">{event.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Event Configuration */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">
          Configure {currentEvent?.name}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Event Payload (JSON)
            </label>
            <textarea
              value={eventPayload}
              onChange={(e) => setEventPayload(e.target.value)}
              className="w-full h-48 bg-black text-green-400 font-mono text-sm p-3 rounded border border-gray-600 focus:border-green-400 focus:outline-none"
              placeholder={JSON.stringify(currentEvent?.sample, null, 2)}
            />
          </div>
          <button
            onClick={simulateEvent}
            className="px-6 py-2 bg-green-600 text-black rounded-lg hover:bg-green-500 transition-colors font-bold"
          >
            Simulate Event
          </button>
        </div>
      </div>

      {/* Response */}
      {response && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-green-400 mb-4">Response</h3>
          <pre className="bg-black p-4 rounded text-sm text-gray-300 overflow-x-auto">
            {response}
          </pre>
        </div>
      )}

      {/* Event History */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Event History</h3>
        {history.length > 0 ? (
          <div className="space-y-2">
            {history.slice(-5).reverse().map((item, idx) => (
              <div key={idx} className="flex items-center justify-between bg-black p-3 rounded">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">{item.timestamp}</span>
                  <span className="text-sm">{item.event}</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  item.status === 'success'
                    ? 'bg-green-900 text-green-300'
                    : 'bg-red-900 text-red-300'
                }`}>
                  {item.status}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No events simulated yet</p>
        )}
      </div>
    </div>
  );
};

export default EventSimulator;